import styled, { keyframes } from "styled-components";
import { ThinkingRing, ThinkingText } from "../ProcessingMessage/Styles";
import { SUGGESTION_RISK, type SuggestionRisk } from "@dd-report-components";

export const MiniThinkingRing = styled(ThinkingRing)`
  width: 13px;
  height: 13px;
`;

export const MiniThinkingText = styled(ThinkingText)`
  font-size: 0.7rem;
  font-weight: 400;
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export type WidgetSize = "small" | "medium";

const widgetDimensions = ({ $size }: { $size: WidgetSize }) =>
  $size === "medium"
    ? `
      width: 480px;
      height: 680px;
    `
    : `
      width: 360px;
      height: 480px;
    `;

export const WidgetContainer = styled.div<{ $size: WidgetSize }>`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  ${widgetDimensions}
  background: #ffffff;
  border-radius: 1rem;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 9000;
  animation: ${slideInUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  transition: width 0.25s ease, height 0.25s ease;

  @media (max-width: 480px) {
    width: calc(100vw - 2rem);
    right: 1rem;
    bottom: 1rem;
  }

  @media (max-height: 800px) {
    ${({ $size }) => $size === "medium" && "height: calc(100vh - 3rem);"}
  }
`;

export const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #001d30;
  color: #ffffff;
  flex-shrink: 0;
`;

export const WidgetBubble = styled.div`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #001d30;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  animation: ${slideInUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  transition: transform 0.2s, box-shadow 0.2s;

  img {
    width: 34px;
    height: 34px;
    object-fit: contain;
  }

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.35);
  }

  @media (max-width: 480px) {
    right: 1rem;
    bottom: 1rem;
  }
`;

export const WidgetBubbleClose = styled.button`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ef4444;
  border: 2px solid #ffffff;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  opacity: 0;
  transition: opacity 0.15s;

  ${WidgetBubble}:hover & {
    opacity: 1;
  }
`;

export const WidgetLogo = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #001d30;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;

export const WidgetTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  flex: 1;
`;

export const WidgetActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const WidgetIconButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

export const WidgetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: #f9fafb;
`;

export const WidgetMessage = styled.div<{ $isBot: boolean }>`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  flex-direction: ${({ $isBot }) => ($isBot ? "row" : "row-reverse")};
`;

export const WidgetMessageBubble = styled.div<{ $isBot: boolean }>`
  max-width: 78%;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ $isBot }) =>
    $isBot ? "0.125rem 0.75rem 0.75rem 0.75rem" : "0.75rem 0.125rem 0.75rem 0.75rem"};
  background: ${({ $isBot }) => ($isBot ? "#ffffff" : "#001d30")};
  color: ${({ $isBot }) => ($isBot ? "#111827" : "#ffffff")};
  font-size: 0.8125rem;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  word-break: break-word;
  white-space: pre-line;
`;

export const WidgetMiniAvatar = styled.div<{ $isBot: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }
`;

export const WidgetDots = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 0.25rem 0;
`;

export const WidgetDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9ca3af;
  display: inline-block;
  animation: dotPulse 1.4s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes dotPulse {
    0%, 60%, 100% { transform: scale(1); opacity: 0.5; }
    30% { transform: scale(1.3); opacity: 1; }
  }
`;

export const WidgetInputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  flex-shrink: 0;
`;

export const WidgetInput = styled.textarea`
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  outline: none;
  transition: border-color 0.2s;
  resize: none;
  min-height: 2.25rem;
  max-height: 6rem;
  overflow-y: auto;
  line-height: 1.5;
  font-family: inherit;

  &:focus {
    border-color: #001d30;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const WidgetSendButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #001d30;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.1s;

  &:hover {
    background: #1f2937;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
`;

export const DocumentMessageWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
`;

export const DocumentMessageIconWrapper = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 0.2rem;
  background-color: #e5edf1;

  > svg {
    height: 80%;
    width: 80%;
    color: #001d30;
  }
`;

export const DocumentMessageName = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
`;

export type ChipRisk = SuggestionRisk;

const chipBorderColor = (risk: ChipRisk | undefined): string => {
  switch (risk) {
    case SUGGESTION_RISK.red:
      return "#E5533C";
    case SUGGESTION_RISK.amber:
      return "#E8A33D";
    case SUGGESTION_RISK.neutral:
      return "#e5e7eb";
    default:
      return "#12B886";
  }
};

const chipHoverColor = (risk: ChipRisk | undefined): string => {
  switch (risk) {
    case SUGGESTION_RISK.red:
      return "#FBE7E3";
    case SUGGESTION_RISK.amber:
      return "#FBF1E1";
    case SUGGESTION_RISK.neutral:
      return "#f9fafb";
    default:
      return "#E6F7F1";
  }
};

export const SuggestionsTitle = styled.div`
  font-size: 0.6875rem;
  color: #64757f;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin: 0.125rem 0.125rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

export const SuggestionsSpark = styled.span`
  color: #12b886;
`;

export const SuggestionChips = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: ${({ $inline }) => ($inline ? "0.25rem 0 0.25rem 1.875rem" : "0 0 0.625rem")};
`;

export const SuggestionChip = styled.button<{ $risk?: ChipRisk }>`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  min-height: 3.25rem;
  background: #ffffff;
  border: 1.5px solid ${({ $risk }) => chipBorderColor($risk)};
  color: #0b2a3c;
  font-size: 0.875rem;
  line-height: 1.35;
  font-family: inherit;
  padding: 0.75rem 0.875rem;
  border-radius: 0.75rem;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s;

  &:hover {
    background: ${({ $risk }) => chipHoverColor($risk)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const SuggestionChipDot = styled.span<{ $risk?: ChipRisk }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 0.5rem;
  background: ${({ $risk }) => ($risk === SUGGESTION_RISK.neutral ? "#9ca3af" : chipBorderColor($risk))};
`;

export const SuggestionChipText = styled.span`
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const SuggestionChipArrow = styled.span<{ $risk?: ChipRisk }>`
  margin-left: auto;
  margin-top: 0.0625rem;
  font-size: 0.875rem;
  opacity: 0.7;
  flex-shrink: 0;
  color: ${({ $risk }) => ($risk === SUGGESTION_RISK.neutral ? "#64757f" : chipBorderColor($risk))};
`;

export const DocumentDownloadButton = styled.div`
  width: 1.1rem;
  height: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-left: 0.5rem;

  > svg {
    height: 100%;
    width: 100%;
    color: #001d30;
    cursor: pointer;
  }
`;
